import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'
import { css } from 'styled-components'

import { Data } from '@blade/std-lib'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import { type InviteCode } from '@root/entities/referral'
import { reatomUseInviteCodeForm } from '@root/features/referral/atoms/reatom-use-invite-code-form'
import { useTranslation } from '@root/shared/localization'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

import { InviteCodeForm } from '../common/invite-code-form'
import { InviteCodeUserInfo } from '../common/invite-code-user-info'

type Props = {
  existingInviteCode: InviteCode
  initialInviteCode: InviteCode | undefined
  onDone: () => void
  onCancel: () => void
}

export const EditInviteCodeDialog = ({
  initialInviteCode,
  onDone,
  onCancel,
  existingInviteCode,
}: Props) => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUseInviteCodeForm({ initialInviteCode, existingInviteCode }),
    [initialInviteCode, existingInviteCode],
  )

  const [showCode] = useAtom(model.showCodeAtom)

  return (
    <DialogAnnouncement
      css={css`
        --announcement-header-background: var(--appearanceA-7);
        --announcement-body-background: var(--background-1);
      `}
      withSeparator
      header={
        <Header>
          {showCode ? (
            <>
              <Title>{t('referral.change-invite-code')}</Title>
              <SubTitle>
                {Data.match.I(showCode)({
                  ExistingShowCode: () => t('referral.current-code-user-label'),
                  NewShowCode: () => t('referral.new-code-user-label'),
                })}
              </SubTitle>
              <Code>
                <CurrencyNetworkIcon network={network} size={25} />
                {showCode.inviteCode}
              </Code>
            </>
          ) : undefined}

          <InviteCodeUserInfo inviterStateAtom={model.getInviterState} />
        </Header>
      }
    >
      <Form
        device="desktop"
        model={model}
        existingInviteCode={existingInviteCode}
        onDone={onDone}
        onCancel={onCancel}
      />
    </DialogAnnouncement>
  )
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  position: relative;
  text-align: center;
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: normal;
  line-height: 140%;
`
const Form = styled(InviteCodeForm)`
  padding: 20px 12px;
`
const SubTitle = styled.h3`
  font-weight: 00;
  font-size: 12px;
  line-height: 16px;
  color: var(--textA-9);

  margin-top: 16px;

  strong {
    color: var(--textA-11);
  }
`

const Code = styled.div`
  color: var(--text-12);
  text-align: center;

  display: grid;
  grid-auto-flow: column;
  gap: 4px;

  font-size: 24px;
  font-weight: 700;
  line-height: 120%;

  justify-content: center;
  align-content: center;
  align-items: center;
`
